import axios from "axios";
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { ElMessage } from "element-plus";
import errorCode from "./errorCode";
import router from "@/router";
import { useUserStore } from "@/stores/user";

// 创建 axios 实例
const service: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 300000,
  validateStatus: function (status) {
    return status >= 200 && status <= 500;
  },
});

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // isToken: false 时跳过 Bearer token 注入（如登录接口）
    if ((config.headers as any).isToken !== false) {
      const userStore = useUserStore();
      if (userStore.token) {
        config.headers.Authorization = `Bearer ${userStore.token}`;
      }
    }
    // 移除自定义 isToken 标记，不发给后端
    delete (config.headers as any).isToken;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (res: AxiosResponse) => {
    const status = Number(res.status) || 200;
    // @ts-ignore
    const message = res.data?.msg || errorCode[status] || errorCode["default"];

    /**
     * 前端设置responseType: 'blob'，下载excel文件，后端返回正常的json无法解析
     * 根据响应回来的信息，data为blob且type为json，将blob转成json
     */
    if (res.data?.type === "application/json") {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const data = JSON.parse(e.target.result);
        ElMessage({
          message: data.msg || message,
          type: "error",
        });
      };
      reader.readAsText(res.data);
    }

    // Token 过期或无效
    if (status === 401) {
      ElMessage({
        message: message,
        type: "error",
      });
      const userStore = useUserStore();
      userStore.logout();
      router.push("/login");
      return;
    }

    // 导入失败的错误信息，单独处理
    if (status === 500 && res.data.code === 2) {
      return res.data;
    }

    if (status !== 200 || res.data.code === 1) {
      ElMessage({
        message: message,
        type: "error",
      });
      return Promise.reject(message);
    }

    // 根据后端约定的 code 判断请求是否成功
    if (
      res.data.code !== undefined &&
      res.data.code !== 0 &&
      res.data.code !== 200
    ) {
      ElMessage.error(res.data.msg || "请求失败");
      return Promise.reject(new Error(message));
    }

    return res.data;
  },
  (error) => {
    const resData = error.response?.data;

    // 处理 responseType 为 blob 时，服务端返回 JSON 格式的错误响应
    if (resData instanceof Blob && resData.type === "application/json") {
      return resData
        .text()
        .then((text) => {
          const data = JSON.parse(text);
          ElMessage.error(data.msg || "请求失败");
          return Promise.reject(new Error(data.msg || "请求失败"));
        })
        .catch(() => {
          // JSON 解析失败时降级处理
          return Promise.reject(error);
        });
    }

    const message =
      resData?.msg ||
      resData?.data ||
      resData?.message ||
      error.message ||
      "网络错误";
    ElMessage.error(message);

    if (error.response?.status === 401) {
      const userStore = useUserStore();
      userStore.logout();
      router.push("/login");
    }

    return Promise.reject(error);
  }
);

/** 封装 GET 请求 */
export function get<T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  return service.get(url, config).then((res) => res as unknown as T);
}

/** 封装 POST 请求 */
export function post<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  return service.post(url, data, config).then((res) => res as unknown as T);
}

/** 封装 PUT 请求 */
export function put<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  return service.put(url, data, config).then((res) => res as unknown as T);
}

/** 封装 DELETE 请求 */
export function del<T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  return service
    .delete(url, { ...config, data })
    .then((res) => res as unknown as T);
}

export default service;
